import { FormattedMessage } from "react-intl";
import { CHILD_COUNT_MAX, IMPORT_FIELD_MAX_CHARS, type ImportFieldError } from "@/lib/import-field-validation";

// Shared inline validation warning for free-text person_detail fields: over-length, or an invalid
// email / phone / website. Used by BOTH the in-line merge review and the normal profile editor
// (SAVE · Stammdaten), so the same value reads the same way whichever path it arrives on. Rendered
// under the field, neutral field-meta text only — never a red/accent border (neutral-state-signals
// rule). Not role="alert": a blocked save can reveal several at once; the bar carries the single
// assertive cue. Show/hide timing lives in useFieldWarning.
export function MergeFieldWarning({
  fieldKey,
  error,
  max,
}: {
  fieldKey: string;
  error: ImportFieldError | null;
  // Explicit cap, for collection row fields whose ceiling lives in ROW_FIELD_MAX_CHARS rather than the
  // person_detail table this component looks up by default.
  max?: number;
}) {
  if (!error) return null;
  return (
    <div className="field-meta mt-0.5 text-ink/55" data-testid="profile-merge-field-warning">
      {error === "too-long" ? (
        <FormattedMessage
          id="profile.merge.field.tooLong"
          defaultMessage="Keep this to {max} characters or fewer."
          values={{ max: max ?? IMPORT_FIELD_MAX_CHARS[fieldKey] ?? 0 }}
        />
      ) : error === "invalid-email" ? (
        <FormattedMessage id="profile.merge.field.invalidEmail" defaultMessage="Enter a valid email address." />
      ) : error === "invalid-url" ? (
        <FormattedMessage id="profile.merge.field.invalidUrl" defaultMessage="Enter a valid web address." />
      ) : error === "child-count-negative" ? (
        <FormattedMessage
          id="profile.merge.field.childCountNegative"
          defaultMessage="The number of children cannot be negative."
        />
      ) : error === "child-count-max" ? (
        <FormattedMessage
          id="profile.merge.field.childCountMax"
          defaultMessage="The number of children cannot exceed {max}."
          values={{ max: CHILD_COUNT_MAX }}
        />
      ) : (
        <FormattedMessage id="profile.merge.field.invalidPhone" defaultMessage="Enter a valid phone number." />
      )}
    </div>
  );
}
